import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import { getProjectEmployeeWorkloadsAPI } from '@/services/apis/dashboard';
import { useRequest } from "ahooks"
import { Spin } from "antd"
import Decimal from 'decimal.js';

// 颜色配置
const COLORS = {
    fillRate: '#3b82f6',       // 蓝色 - 填报率
    saturation: '#4b5563',     // 深灰色 - 项目饱和度
    effectiveSaturation: '#10b981', // 绿色 - 有效项目饱和度
    totalWorkHours: '#06b6d4',     // 青色 - 工时占比
};

// 图例组件
const Legend: React.FC = () => {
    const legendItems = [
        { key: 'fillRate', label: '工时填报率', color: COLORS.fillRate },
        { key: 'projectSaturation', label: '项目饱和度', color: COLORS.saturation },
        { key: 'totalWorkHours', label: '本项目工时占总项目工时占比', color: COLORS.totalWorkHours },
    ];

    return (
        <div className="flex flex-wrap gap-4 mb-6 justify-center">
            {legendItems.map((item) => (
                <div key={item.key} className="flex items-center">
                    {/* {item.isLine ? (
                        <>
                            <div className="w-6 h-0.5 mr-2" style={{ backgroundColor: item.color }} />
                            <div className="w-2 h-2 rounded-full mr-2" style={{ backgroundColor: item.color }} />
                        </>
                    ) : ( */}
                        <div
                            className="w-4 h-4 mr-2 rounded"
                            style={{ backgroundColor: item.color }}
                        />
                    {/* )} */}
                    <span className="text-sm text-gray-700">{item.label}</span>
                </div>
            ))}
        </div>
    );
};

// 主图表组件
const MemberDataChart: React.FC<{ projectID: number | string, departs: string[] }> = ({ projectID, departs }) => {
    const svgRef = useRef<SVGSVGElement>(null);
    const yAxisLeftRef = useRef<SVGSVGElement>(null);
    const chartContainerRef = useRef<HTMLDivElement>(null);
    const [data, setData] = useState<API.Project.DashboardUserWorkloads[]>([]);
    const [dimensions, setDimensions] = useState({ width: 800, height: 500 });
    const [hoveredMember, setHoveredMember] = useState<string | null>(null);
    const [tooltip, setTooltip] = useState<{
        visible: boolean;
        x: number;
        y: number;
        content: string;
    }>({ visible: false, x: 0, y: 0, content: '' });
    const tooltipTimerRef = useRef<number | null>(null);

    const { loading, run: fetchMemberData } = useRequest(getProjectEmployeeWorkloadsAPI, {
        manual: true,
        onSuccess: (res) => {
            setData((res || []).map(v => ({
                ...v,
                totalWorkHours: Number(Decimal(v.totalWorkHours || 0).toFixed(2)),
            })));
            // 处理其他成功逻辑
        }
    });

    useEffect(() => {
        fetchMemberData(projectID, departs);
    }, [projectID, departs, fetchMemberData]);

    // 响应式调整尺寸
    useEffect(() => {
        const updateDimensions = () => {
            if (chartContainerRef.current) {
                const containerWidth = chartContainerRef.current.clientWidth;
                // 根据数据量计算最小宽度，每个人至少100px
                const minChartWidth = Math.max(data.length * 100, 800);
                setDimensions({
                    width: Math.max(containerWidth, minChartWidth),
                    height: 500
                });
            }
        };

        updateDimensions();
        window.addEventListener('resize', updateDimensions);
        return () => window.removeEventListener('resize', updateDimensions);
    }, [data.length]);

    // 绘制图表
    useEffect(() => {
        if (!svgRef.current || !yAxisLeftRef.current || !data.length || dimensions.width === 0) return;

        // 清除之前的图表
        d3.select(svgRef.current).selectAll('*').remove();
        d3.select(yAxisLeftRef.current).selectAll('*').remove();

        const svg = d3.select(svgRef.current);
        const svgLeft = d3.select(yAxisLeftRef.current);
        
        const { width, height } = dimensions;
        const margin = { top: 50, right: 0, bottom: 100, left: 10 };
        const yAxisWidth = 60;
        const chartWidth = width;
        const chartHeight = height - margin.top - margin.bottom;

        // 创建主图表区域
        const chartGroup = svg
            .append('g')
            .attr('transform', `translate(${margin.left}, ${margin.top})`);

        // X轴比例尺（姓名）
        const xScale = d3
            .scaleBand()
            .domain(data.map(d => d.username))
            .range([0, chartWidth])
            .padding(0.4);

        // 分组比例尺 - 用于柱状图分组
        const subgroups = ['fillRate', 'projectSaturation', 'totalWorkHours'];
        const xSubgroup = d3
            .scaleBand()
            .domain(subgroups)
            .range([0, xScale.bandwidth()])
            .padding(0.1);

        let maxY = 100
        data.forEach(d => {
            maxY = Math.max(maxY, d.fillRate, d.projectSaturation)
        })
        if(maxY > 100) {
            maxY += 10
        }
        // Y轴比例尺（百分比）
        const yScale = d3
            .scaleLinear()
            .domain([0, maxY])
            .range([chartHeight, 0])
            .nice();

        // 绘制左侧固定Y轴
        const yAxisGroup = svgLeft
            .append('g')
            .attr('transform', `translate(${yAxisWidth}, ${margin.top})`)
            .call(d3.axisLeft(yScale).ticks(10).tickFormat(d => `${d}%`));

        yAxisGroup.selectAll('text')
            .attr('class', 'text-xs text-gray-600');

        yAxisGroup.selectAll('path, line')
            .attr('class', 'stroke-gray-300');

        // Y轴标签
        svgLeft
            .append('text')
            .attr('transform', 'rotate(-90)')
            .attr('y', 15)
            .attr('x', -(chartHeight / 2 + margin.top))
            .attr('text-anchor', 'middle')
            .attr('class', 'text-sm font-medium text-gray-700')
            .text('百分比 (%)');

        // 添加X轴
        const xAxisGroup = chartGroup
            .append('g')
            .attr('transform', `translate(0, ${chartHeight})`)
            .call(d3.axisBottom(xScale));

        xAxisGroup.selectAll('text')
            .attr('class', 'text-xs text-gray-600')
            .attr('dy', '1em')
            .attr('dx', '-0.5em')
            .style('text-anchor', 'middle')

        xAxisGroup.selectAll('path, line')
            .attr('class', 'stroke-gray-300');

        // 添加X轴标题
        svg
            .append('text')
            .attr('x', width / 2)
            .attr('y', height - 20)
            .attr('text-anchor', 'middle')
            .attr('class', 'text-sm font-medium text-gray-700')
            .text('项目成员姓名');

        // 添加水平网格线（去掉最左侧的竖线）
        const gridGroup = chartGroup
            .append('g')
            .attr('class', 'grid')
            .call(
                d3.axisLeft(yScale)
                    .tickSize(-chartWidth)
                    .tickFormat(() => '')
            );

        gridGroup.selectAll('line')
            .attr('class', 'stroke-gray-200 stroke-1')
            .attr('stroke-dasharray', '3,3');
        
        // 隐藏Y轴的主轴线（避免黑线）
        gridGroup.select('path').remove();

        // 创建柱状图组
        const barsGroup = chartGroup.append('g');

        // 按用户绘制柱状图，每个用户绘制两个柱子
        data.forEach((d) => {
            subgroups.forEach((subgroup, subgroupIndex) => {
                const value = d[subgroup as keyof API.Project.DashboardUserWorkloads] as number;
                const xPos = (xScale(d.username) || 0) + (xSubgroup(subgroup) || 0);
                const yPos = yScale(value);
                
                barsGroup
                    .append('rect')
                    .attr('class', `bar bar-${subgroup} transition-all duration-200 cursor-pointer`)
                    .attr('x', xPos)
                    .attr('y', yPos)
                    .attr('width', xSubgroup.bandwidth())
                    .attr('height', chartHeight - yPos)
                    .attr('fill', COLORS[subgroup as keyof typeof COLORS])
                    .attr('rx', 2)
                    .on('mouseenter', function (event) {
                        if (tooltipTimerRef.current) {
                            clearTimeout(tooltipTimerRef.current);
                            tooltipTimerRef.current = null;
                        }
                        
                        const labels: Record<string, string> = {
                            fillRate: '填报率',
                            projectSaturation: '项目饱和度',
                            totalWorkHours: '本项目工时占总项目工时占比',
                        };

                        d3.select(this)
                            .attr('opacity', 0.7)
                            .attr('filter', 'brightness(1.2)');

                        setHoveredMember(d.username);
                        setTooltip({
                            visible: true,
                            x: event.pageX + 10,
                            y: event.pageY - 10,
                            content: `
                  <div class="font-semibold text-white mb-1">${labels[subgroup]}</div>
                  <div class="text-white text-xs mb-1">${d.username}</div>
                  <div class="text-white font-medium">${value}%</div>
                `
                        });
                    })
                    .on('mouseleave', function () {
                        d3.select(this)
                            .attr('opacity', 1)
                            .attr('filter', null);

                        setHoveredMember(null);
                        tooltipTimerRef.current = window.setTimeout(() => {
                            setTooltip(prev => ({ ...prev, visible: false }));
                        }, 100);
                    });
            });

            const y1 = yScale(d.fillRate);
            const y2 = yScale(d.projectSaturation);
            const y3 = yScale(d.totalWorkHours);
            
            const labelHeight = 14;
            const minSpacing = 8;
            
            interface LabelData {
                key: string;
                y: number;
                originalY: number;
                subgroup: string;
            }
            
            const labels: LabelData[] = [
                { key: 'fillRate', y: y1 - 5, originalY: y1 - 5, subgroup: 'fillRate' },
                { key: 'projectSaturation', y: y2 - 5, originalY: y2 - 5, subgroup: 'projectSaturation' },
                { key: 'totalWorkHours', y: y3 - 5, originalY: y3 - 5, subgroup: 'totalWorkHours' }
            ];
            
            labels.sort((a, b) => b.originalY - a.originalY);
            
            for (let i = 0; i < labels.length - 1; i++) {
                const lower = labels[i];
                const upper = labels[i + 1];
                if (upper.y >= lower.y - labelHeight - minSpacing) {
                    upper.y = lower.y - labelHeight - minSpacing;
                }
            }
            
            const topLimit = 15;
            labels.forEach(label => {
                if (label.y < topLimit) {
                    label.y = topLimit;
                }
            });
            
            labels.sort((a, b) => b.y - a.y);
            for (let i = 0; i < labels.length - 1; i++) {
                const lower = labels[i];
                const upper = labels[i + 1];
                if (upper.y >= lower.y - labelHeight - minSpacing) {
                    upper.y = lower.y - labelHeight - minSpacing;
                    if (upper.y < topLimit) {
                        upper.y = topLimit;
                    }
                }
            }
            
            labels.forEach(label => {
                const x = (xScale(d.username) || 0) + (xSubgroup(label.subgroup as any) || 0) + xSubgroup.bandwidth() / 2;
                const value = d[label.key as keyof API.Project.DashboardUserWorkloads] as number;
                
                chartGroup
                    .append('text')
                    .attr('class', 'data-label text-xs font-medium')
                    .attr('x', x)
                    .attr('y', label.y)
                    .attr('text-anchor', 'middle')
                    .text(`${value}%`)
                    .style('fill', '#4b5563')
                    .style('pointer-events', 'none');
            });
        });

    }, [data, dimensions, hoveredMember]);

    return (
        <div className="px-4">
            <div className="mx-auto">
                {/* 头部 */}
                <h3 className="text-sm font-semibold text-gray-700 mb-4">项目成员数据统计分析</h3>
                <Spin spinning={loading}>
                    {/* 图表容器 */}
                    {data.length > 0 && (
                    <div className="bg-white">
                        <Legend />
                        
                            <div className="flex w-full" style={{ minHeight: '550px' }}>
                                {/* 左侧固定Y轴 */}
                                <div
                                    className="sticky left-0 z-10 bg-white shrink-0"
                                    style={{ width: '60px', height: dimensions.height }}
                                >
                                    <svg
                                        ref={yAxisLeftRef}
                                        width="60"
                                        height={dimensions.height}
                                    />
                                </div>

                                {/* 中间可滚动图表区 */}
                                <div
                                    ref={chartContainerRef}
                                    className="overflow-x-auto grow"
                                >
                                    <svg
                                        ref={svgRef}
                                        width={dimensions.width}
                                        height={dimensions.height}
                                        className="mx-auto"
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                </Spin>
                {!loading && data.length <= 0 && <div className="text-center text-xs text-gray-500 h-[200px] flex items-center justify-center">暂无数据</div>}
            </div>

            {/* 工具提示 */}
            {tooltip.visible && (
                <div
                    className="fixed z-50 bg-gray-900 text-white p-3 rounded-lg shadow-lg text-xs pointer-events-none"
                    style={{
                        left: `${tooltip.x - window.scrollX}px`,
                        top: `${tooltip.y - window.scrollY}px`,
                        maxWidth: '200px',
                    }}
                    dangerouslySetInnerHTML={{ __html: tooltip.content }}
                />
            )}
        </div>
    );
};

export default MemberDataChart;